import { Box, ClickAwayListener, IconButton, useTheme } from "@mui/material";
import SidebarItem from "components/apply-loan/SideBarItem";
import { useDispatch, useSelector } from "react-redux";
import {
  rndRouteToStepMapping,
  sblRouteToStepMapping,
  useProgress,
} from "context/ProgressProvider";
import { useEffect, useRef, useState } from "react";
import { ProfileActions } from "state/profile/Action";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, DragHandle } from "@mui/icons-material";
import { LoanService } from "services/LoanService";

const ApplyLoan = () => {
  const { syncProgressWithRoute, currentProfile } = useProgress();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const previousPathRef = useRef<string>("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const theme = useTheme();
  const authInfo = useSelector((state: any) => state.authState);

  const isSblApplyRoute = /^\/sbl(\/|$)/.test(location.pathname);
  const valuePath = isSblApplyRoute ? "sbl" : "rnd";

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.id) {
        if (target.id === "menu-navigation") {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (previousPathRef.current !== location.pathname) {
      syncProgressWithRoute(location.pathname);
      previousPathRef.current = location.pathname;
    }
  }, [
    location.pathname,
    dispatch,
    navigate,
    syncProgressWithRoute,
    currentProfile.isPayment,
  ]);

  useEffect(() => {
    if (authInfo.selectedLoan) {
      const item = authInfo.rowLoanStatus.find(
        (r) => r.id === authInfo.selectedLoan
      );

      try {
        if (valuePath !== item.value) {
          navigate(`/${item.value}/intro-details`);
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [authInfo.selectedLoan, location.pathname]);

  return (
    <Box display="flex" minHeight="100vh" bgcolor="white">
      {/* Sidebar */}

      <ClickAwayListener onClickAway={() => setSidebarOpen(false)}>
        <Box
          sx={{
            overflowY: "auto",
            minWidth: "300px",
            position: "relative",
            display: "flex",
            zIndex: 1000,
            background: { xs: "transparent", md: "#f3f3f5" },
            [theme.breakpoints.down("md")]: {
              transform: sidebarOpen ? "translateX(0)" : "translateX(-83%)",
              position: "absolute",
            },
          }}
        >
          <SidebarItem setSidebarOpen={setSidebarOpen} />
          <Box
            id="menu-navigation"
            sx={{
              position: "relative",
              zIndex: 998,
              display: { xs: "block", md: "none" },
            }}
          >
            <Box
              id="box-icon"
              sx={{
                borderRadius: "6px",
                border: !sidebarOpen
                  ? "2px solid #e7e7e7"
                  : "1px solid #f3f3f5",
                display: "flex",
                padding: "0.2rem 0",
                left: "-1.5rem",
                background: "#f3f3f5",
                position: "relative",
                alignItems: "center",
                cursor: "pointer",
                top: "2.083vw",
              }}
              onClick={() => setSidebarOpen((prev) => !prev)}
            >
              <ArrowRight
                sx={{
                  position: "relative",
                  left: "1rem",
                  color: "black",
                  opacity: 0.4,
                }}
                fontSize="large"
              />
              <DragHandle
                sx={{
                  transform: "rotate(90deg)",
                  color: "black",
                  opacity: 0.4,
                }}
                fontSize="medium"
              />
            </Box>
          </Box>
        </Box>
      </ClickAwayListener>

      {/* Main Content */}
      <Box
        sx={{
          padding:
            location.pathname === "/sbl/verif-review/2" ||
            location.pathname === "/sbl/verif-review/3" ||
            location.pathname === "/rnd/review-approval/2" ||
            location.pathname === "/rnd/review-approval/3"
              ? "0 3.75vw"
              : (currentProfile.isPayment &&
                  (location.pathname === "/sbl/verif-review" ||
                    location.pathname === "/rnd/review-approval")) ||
                (currentProfile.isPayment &&
                  (location.pathname === "/sbl/final-agreements" ||
                    location.pathname === "/rnd/final-agreements"))
              ? "0 3.75vw"
              : {
                  xs: "2.083vw 3.75vw 2.083vw 8.75vw",
                  md: "2.083vw 3.75vw",
                },
          width:
            location.pathname === "/sbl/verif-review/2" ||
            location.pathname === "/sbl/verif-review/3" ||
            location.pathname === "/rnd/review-approval/2" ||
            location.pathname === "/rnd/review-approval/3"
              ? "100%"
              : (currentProfile.isPayment &&
                  (location.pathname === "/sbl/verif-review" ||
                    location.pathname === "/rnd/review-approval")) ||
                (currentProfile.isPayment &&
                  (location.pathname === "/sbl/final-agreements" ||
                    location.pathname === "/rnd/final-agreements"))
              ? "100%"
              : "auto",
          // Payment Mobile
          [theme.breakpoints.down("md")]: {
            maxHeight:
              location.pathname === "/sbl/verif-review" ||
              location.pathname === "/rnd/review-approval"
                ? "calc(100vh - 2vw)"
                : location.pathname === "/sbl/final-solicitor" ||
                  location.pathname === "/rnd/final-accountant"
                ? "calc(100vh - 2vw)"
                : location.pathname === "/sbl/final-agreements" ||
                  location.pathname === "/rnd/final-agreements"
                ? "calc(100vh - 2vw)"
                : location.pathname === "/sbl/verif-review/2" ||
                  location.pathname === "/rnd/review-approval/2"
                ? "100vh"
                : "unset",
            overflowY:
              location.pathname.includes("/sbl/verif-review") ||
              location.pathname.includes("/rnd/review-approval")
                ? "scroll"
                : location.pathname.includes("/sbl/final-solicitor") ||
                  location.pathname.includes("/rnd/final-accountant")
                ? "scroll"
                : location.pathname.includes("/sbl/final-agreements") ||
                  location.pathname.includes("/rnd/final-agreements")
                ? "scroll"
                : "unset",
          },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default ApplyLoan;
